import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getWarehouses } from '../../services/warehousesService';
import entryGuideService from '../../services/entryGuideService';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';
import {
  formatGuideEntryDateTimeLima,
  getCurrentDateLima,
  getCurrentTimeLima,
  normalizeTime,
} from '../../utils/dateUtils';
import MutateGate from '../../components/Common/MutateGate';
import { useCanMutate } from '../../hooks/useCanMutate';
import EntryGuidePdfButton from '../../components/EntryGuides/EntryGuidePdfButton';
import EntryGuidePrintButton from '../../components/EntryGuides/EntryGuidePrintButton';
import ReceptionConsumeButton from '../../components/EntryGuides/ReceptionConsumeButton';
import ReceptionConsumeModal from '../../components/EntryGuides/ReceptionConsumeModal';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { isReceptionConsumeAutomationActive } from '../../utils/receptionConsumeAutomation';
import { downloadWarehouseGuidesExcel } from '../../utils/warehouseGuidesExcelExport';
import {
  formatDateWarningsTooltip,
  hasDateWarnings,
} from '../../utils/entryGuideDateAnomalies';

const GUIDES_PAGE_SIZE = 100;

const getTransferWriteOffEntryGuide = (transfer) => (
  transfer?.transfer_writeoff_entry_guide
  || transfer?.transferWriteoffEntryGuide
  || null
);

const getTransferWriteOffExitGuide = (transfer) => (
  transfer?.transfer_writeoff_exit_guide
  || transfer?.transferWriteoffExitGuide
  || null
);

const getTransferWriteOffInfo = (transfer) => {
  const entryGuide = getTransferWriteOffEntryGuide(transfer);
  if (!entryGuide && !transfer?.transfer_writeoff_entry_guide_id) {
    return null;
  }

  const exitGuide = getTransferWriteOffExitGuide(transfer);
  const items = (entryGuide?.items || []).map((item) => ({
    product_id: item.product_id,
    product_name: item.product?.name || item.description || 'N/A',
    product_sku: item.product?.sku || 'N/A',
    quantity: parseFloat(item.quantity ?? 0),
    converted_quantity: parseFloat(item.converted_quantity ?? item.quantity ?? 0),
    unit_of_measure: item.unit_of_measure || item.base_unit_of_measure || 'UN',
    condition: item.condition || 'good',
  }));

  return {
    entry_code: entryGuide?.code || null,
    exit_code: exitGuide?.code || null,
    registered_date: entryGuide?.entry_date || null,
    registered_time: entryGuide?.entry_time || null,
    items,
  };
};

const guideMatchesFilter = (doc, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const parts = [
    doc.code,
    doc.supplier,
    doc.created_by,
    doc.from_warehouse,
    doc.to_warehouse,
    doc.transfer_guide_code,
    doc.reception_source_code,
    doc.consume_exit_guide_code,
    doc.sales_order_code,
    doc.status,
    ...(Array.isArray(doc.references)
      ? doc.references.flatMap((ref) => [ref.label, ref.code].filter(Boolean))
      : []),
  ];

  return parts
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(q);
};

const mapGuideDateTime = (guide) => ({
  entry_date: guide.entry_date || guide.issue_date,
  entry_time: guide.entry_time || null,
});

const mapCommonGuideFields = (guide) => ({
  date_warnings: guide.date_warnings || [],
  created_at: guide.created_at || null,
});

const WarehouseDocuments = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const canMutate = useCanMutate();
  const {
    receptionConsumeButtonEnabled,
    receptionConsumeWarehouseIds,
    ventasEnabled,
    kardexAuditModuleEnabled,
  } = useModuleSettings();
  const showReceptionConsumeButton = isReceptionConsumeAutomationActive({
    receptionConsumeButtonEnabled,
    receptionConsumeWarehouseIds,
    warehouseId: id,
  });
  const [warehouse, setWarehouse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('entrada'); // 'entrada', 'salida', 'traspaso', 'recepcion', 'inicial', 'ajuste'
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState({ id: null, code: null });
  const [isPendingItemsModalOpen, setIsPendingItemsModalOpen] = useState(false);
  const [consumeModalGuide, setConsumeModalGuide] = useState(null);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [pendingItems, setPendingItems] = useState([]);
  const [isWriteOffConfirmOpen, setIsWriteOffConfirmOpen] = useState(false);
  const [isRevokeWriteOffConfirmOpen, setIsRevokeWriteOffConfirmOpen] = useState(false);
  const [writeOffLoading, setWriteOffLoading] = useState(false);
  const [writeOffEntryDate, setWriteOffEntryDate] = useState('');
  const [writeOffEntryTime, setWriteOffEntryTime] = useState('');
  const [documents, setDocuments] = useState({
    entrada: [],
    salida: [],
    traspaso: [],
    recepcion: [],
    inicial: [],
    ajuste: [],
  });
  const [guideFilter, setGuideFilter] = useState('');
  const [onlyDateWarnings, setOnlyDateWarnings] = useState(false);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [exportingExcel, setExportingExcel] = useState(false);
  const loadRequestRef = useRef(0);

  // Cargar almacén al cambiar de bodega
  useEffect(() => {
    if (!id) return;

    setGuideFilter('');
    setOnlyDateWarnings(false);
    setSelectedYear(new Date().getFullYear());
    loadWarehouse();
  }, [id]);

  // Cargar guías del año seleccionado
  useEffect(() => {
    if (!id) return;

    loadDocuments(selectedYear);
  }, [id, selectedYear]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, guideFilter, onlyDateWarnings, selectedYear]);

  const filteredDocuments = useMemo(() => {
    const list = documents[activeTab] || [];
    return list.filter((doc) => {
      if (onlyDateWarnings && !hasDateWarnings(doc)) {
        return false;
      }
      return guideMatchesFilter(doc, guideFilter);
    });
  }, [documents, activeTab, guideFilter, onlyDateWarnings]);

  const dateWarningCount = useMemo(
    () => Object.values(documents).flat().filter((doc) => hasDateWarnings(doc)).length,
    [documents]
  );

  const totalPages = Math.max(1, Math.ceil(filteredDocuments.length / GUIDES_PAGE_SIZE));

  const paginatedDocuments = useMemo(() => {
    const start = (currentPage - 1) * GUIDES_PAGE_SIZE;
    return filteredDocuments.slice(start, start + GUIDES_PAGE_SIZE);
  }, [filteredDocuments, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const loadWarehouse = async () => {
    try {
      const response = await getWarehouses({ active_only: true });
      
      if (response.success) {
        const foundWarehouse = response.data?.find(w => w.id === parseInt(id));
        if (foundWarehouse) {
          setWarehouse(foundWarehouse);
        } else {
          setToast({
            isOpen: true,
            message: 'Almacén no encontrado',
            type: 'error'
          });
          setTimeout(() => navigate('/warehouses'), 2000);
        }
      }
    } catch (error) {
      console.error('Error al cargar almacén:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar el almacén',
        type: 'error'
      });
    }
  };

  const loadDocuments = async (year) => {
    if (!id) {
      return;
    }

    const requestId = ++loadRequestRef.current;

    try {
      setLoading(true);
      const response = await entryGuideService.getEntryGuides({
        warehouse_id: parseInt(id),
        all_types: true,
        year,
      });

      if (requestId !== loadRequestRef.current) {
        return;
      }

      const years = response.meta?.available_years ?? [];
      setAvailableYears(years);

      if (years.length > 0 && !years.includes(year)) {
        setSelectedYear(years[0]);
        return;
      }

      if (!response.success) {
        setDocuments({
          entrada: [],
          salida: [],
          inicial: [],
          traspaso: [],
          recepcion: [],
          ajuste: [],
        });
        return;
      }

      const grouped = response.data;

      // Procesar guías de entrada
      const entrada = (grouped.entrada || []).map(guide => {
        const linkedDocs = Array.isArray(guide.linked_invoice_documents)
          ? guide.linked_invoice_documents
          : [];
        let references = [];

        if (guide.purchase_order?.code) {
          references = [{ label: guide.purchase_order.code, code: null, amount: null }];
        } else if (guide.requirement?.code) {
          references = [{ label: guide.requirement.code, code: null, amount: null }];
        } else if (guide.transfer_guide?.code) {
          references = [{ label: guide.transfer_guide.code, code: null, amount: null }];
        } else if (linkedDocs.length > 0) {
          references = linkedDocs.map((doc) => ({
            label: doc.reference_label || doc.code,
            code: doc.code,
            amount: doc.invoiced_amount,
          }));
        } else if (guide.document) {
          const label = guide.document.series && guide.document.number
            ? `${guide.document.series}-${guide.document.number}`
            : guide.document.series || guide.document.number || guide.document.code || '';
          references = [{
            label,
            code: guide.document.code || null,
            amount: guide.billing_invoiced_amount ?? null,
          }];
        }

        return {
          id: guide.id,
          code: guide.code,
          date: guide.issue_date,
          ...mapGuideDateTime(guide),
          ...mapCommonGuideFields(guide),
          supplier: guide.supplier?.name || 'N/A',
          references,
          total_amount: guide.total_amount,
          billing_reference_amount: guide.billing_reference_amount,
          billing_invoiced_amount: guide.billing_invoiced_amount,
          billing_pending_amount: guide.billing_pending_amount,
          items_count: guide.items_count ?? guide.items?.length ?? 0,
          created_by: guide.creator?.name || 'Sistema',
          is_production_special: Boolean(guide.production_pair_guide_id),
        };
      });

      // Procesar guías de salida
      const salida = (grouped.salida || []).map(guide => ({
        id: guide.id,
        code: guide.code,
        date: guide.issue_date,
        ...mapGuideDateTime(guide),
        ...mapCommonGuideFields(guide),
        supplier: guide.supplier?.name || 'N/A',
        sales_order_code: guide.sales_order?.code || guide.salesOrder?.code || null,
        sales_order_id: guide.sales_order_id || guide.sales_order?.id || guide.salesOrder?.id || null,
        reception_source_guide_id: guide.reception_source_guide?.id || guide.reception_source_guide_id || null,
        reception_source_code: guide.reception_source_guide?.code || null,
        items_count: guide.items_count ?? guide.items?.length ?? 0,
        status: guide.status === 'issued' ? 'completed' : 'pending',
        created_by: guide.creator?.name || 'Sistema',
        is_production_special: Boolean(guide.production_pair_guide_id),
      }));

      // Procesar guías iniciales
      const inicial = (grouped.inicial || []).map(guide => ({
        id: guide.id,
        code: guide.code,
        date: guide.issue_date,
        ...mapGuideDateTime(guide),
        ...mapCommonGuideFields(guide),
        supplier: guide.supplier?.name || 'N/A',
        items_count: guide.items_count ?? guide.items?.length ?? 0,
        status: guide.status === 'issued' ? 'completed' : 'pending',
        created_by: guide.creator?.name || 'Sistema',
        is_production_special: Boolean(guide.production_pair_guide_id),
      }));

      // Procesar traspasos
      const traspaso = (grouped.traspaso || []).map(guide => ({
        id: guide.id,
        code: guide.code,
        date: guide.issue_date,
        ...mapGuideDateTime(guide),
        ...mapCommonGuideFields(guide),
        from_warehouse: guide.warehouse?.name || 'N/A',
        to_warehouse: guide.destination_warehouse?.name || 'N/A',
        items_count: guide.items_count ?? guide.items?.length ?? 0,
        status: guide.status === 'issued' ? 'completed' : 'pending',
        created_by: guide.creator?.name || 'Sistema',
        completion_percentage: guide.completion_percentage || 0,
        has_receptions: Boolean(guide.has_receptions),
        has_writeoff: Boolean(guide.has_writeoff || guide.transfer_writeoff_entry_guide_id),
      }));

      // Procesar recepciones
      const recepcion = (grouped.recepcion || []).map(guide => ({
        id: guide.id,
        code: guide.code,
        date: guide.issue_date,
        ...mapGuideDateTime(guide),
        ...mapCommonGuideFields(guide),
        from_warehouse: guide.transfer_guide?.warehouse?.name || 'N/A',
        to_warehouse: guide.warehouse?.name || 'N/A',
        transfer_guide_code: guide.transfer_guide?.code || 'N/A',
        consume_exit_guide_id: guide.consume_exit_guide?.id || guide.consume_exit_guide_id || null,
        consume_exit_guide_code: guide.consume_exit_guide?.code || null,
        is_consumed: Boolean(guide.consume_exit_guide),
        items_count: guide.items_count ?? guide.items?.length ?? 0,
        status: guide.status === 'issued' ? 'completed' : 'pending',
        created_by: guide.creator?.name || 'Sistema',
        is_production_special: Boolean(guide.production_pair_guide_id),
      }));

      const ajuste = (grouped.ajuste || []).map(guide => ({
        id: guide.id,
        code: guide.code,
        guide_type: 'ajuste',
        date: guide.issue_date,
        ...mapGuideDateTime(guide),
        ...mapCommonGuideFields(guide),
        supplier: guide.supplier?.name || '—',
        items_count: guide.items_count ?? guide.items?.length ?? 0,
        status: guide.status === 'issued' ? 'completed' : 'pending',
        created_by: guide.creator?.name || 'Sistema',
        is_production_special: Boolean(guide.production_pair_guide_id),
      }));

      setDocuments({
        entrada,
        salida,
        inicial,
        traspaso,
        recepcion,
        ajuste,
      });
    } catch (error) {
      console.error('Error al cargar guías:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar las guías',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      entrada: 'Guías de Entrada',
      salida: 'Guías de Salida',
      traspaso: 'Traspasos',
      recepcion: 'Recepciones',
      inicial: 'Guías Iniciales',
      ajuste: 'Ajustes',
    };
    return labels[type] || type;
  };

  const getCreateButtonLabel = () => {
    const labels = {
      entrada: 'Crear guía de entrada',
      salida: 'Crear guía de salida',
      traspaso: 'Crear traspaso',
      recepcion: 'Crear recepción',
      inicial: 'Crear guía inicial',
      ajuste: 'Crear ajuste',
    };
    return labels[activeTab] ? `+ ${labels[activeTab]}` : '+ Crear';
  };

  const getStatusBadge = (status) => {
    if (status === 'completed' || status === 'issued') {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
          Completado
        </span>
      );
    } else if (status === 'pending' || status === 'cancelled') {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
          {status === 'cancelled' ? 'Cancelado' : 'Pendiente'}
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
        {status}
      </span>
    );
  };

  const getGuidePrintPath = (guideId, tab = activeTab, doc = null) => {
    const routes = {
      entrada: `/entry-guides/${guideId}/print`,
      inicial: `/initial-guides/${guideId}/print`,
      salida: `/exit-guides/${guideId}/print`,
      traspaso: `/transfer-guides/${guideId}/print`,
      recepcion: `/reception-guides/${guideId}/print`,
      ajuste: `/entry-guides/${guideId}/print`,
    };
    const base = routes[tab] || null;
    if (!base) return null;
    const guideType = doc?.guide_type || tab;
    const itemsCount = doc?.items_count ?? 0;
    const qs = new URLSearchParams({
      guide_type: guideType,
      items_count: String(itemsCount),
    });
    return `${base}?${qs.toString()}`;
  };

  const handlePrintGuide = (guideId, doc = null) => {
    const path = getGuidePrintPath(guideId, activeTab, doc);
    if (path) {
      window.open(path, '_blank');
      return;
    }
    setToast({
      isOpen: true,
      message: 'Funcionalidad en desarrollo',
      type: 'info',
    });
  };

  const handleConsumeReception = (doc) => {
    setConsumeModalGuide({ id: doc.id, code: doc.code });
  };

  const handleDelete = (docId, docCode) => {
    setPendingDelete({ id: docId, code: docCode });
    setIsDeleteModalOpen(true);
  };

  const handleExportExcel = async () => {
    if (filteredDocuments.length === 0 || exportingExcel) return;

    setExportingExcel(true);
    try {
      await downloadWarehouseGuidesExcel({
        guides: filteredDocuments,
        activeTab,
        warehouse,
        guideFilter,
      });
      setToast({
        isOpen: true,
        message: `Excel exportado (${filteredDocuments.length} guía${filteredDocuments.length === 1 ? '' : 's'})`,
        type: 'success',
      });
    } catch (error) {
      console.error('Error al exportar guías:', error);
      setToast({
        isOpen: true,
        message: 'Error al exportar el Excel',
        type: 'error',
      });
    } finally {
      setExportingExcel(false);
    }
  };

  const calculatePendingItems = (transfer) => {
    if (!transfer.items || !transfer.items.length) {
      return [];
    }

    const lostByProduct = {};
    const writeoffItems = transfer.transfer_writeoff_entry_guide?.items
      || transfer.transferWriteoffEntryGuide?.items
      || [];
    writeoffItems.forEach((lostItem) => {
      const productId = lostItem.product_id;
      if (!productId) return;
      const qty = parseFloat(lostItem.converted_quantity ?? lostItem.quantity ?? 0);
      lostByProduct[productId] = (lostByProduct[productId] || 0) + qty;
    });

    const pending = transfer.items.map((transferItem) => {
      const productId = transferItem.product_id;
      let receivedQuantity = 0;

      if (transfer.receptions && transfer.receptions.length > 0) {
        transfer.receptions.forEach((reception) => {
          if (reception.items && reception.items.length > 0) {
            reception.items.forEach((receptionItem) => {
              if (receptionItem.product_id === productId) {
                receivedQuantity += parseFloat(
                  receptionItem.converted_quantity ?? receptionItem.quantity ?? 0
                );
              }
            });
          }
        });
      }

      const transferQuantity = parseFloat(
        transferItem.converted_quantity ?? transferItem.quantity ?? 0
      );
      const lostQuantity = lostByProduct[productId] || 0;
      const pendingQuantity = Math.max(0, transferQuantity - receivedQuantity - lostQuantity);
      const displayTransferQty = parseFloat(transferItem.quantity || transferQuantity);

      return {
        product_id: productId,
        product_name: transferItem.product?.name || transferItem.description || 'N/A',
        product_sku: transferItem.product?.sku || 'N/A',
        unit_of_measure: transferItem.unit_of_measure || transferItem.base_unit_of_measure || 'UN',
        transfer_quantity: displayTransferQty,
        received_quantity: receivedQuantity,
        lost_quantity: lostQuantity,
        pending_quantity: pendingQuantity,
        unit_price: transferItem.unit_price || 0,
        condition: transferItem.condition || 'good',
      };
    }).filter((item) => item.pending_quantity > 0.001);

    return pending;
  };

  const refreshSelectedTransferPending = async (transferId, transferFallback = null) => {
    const response = await entryGuideService.getEntryGuide(transferId);
    const guide = response?.success ? response.data : null;
    if (!guide) {
      throw new Error('No se pudo cargar el detalle del traspaso');
    }

    const mergedTransfer = {
      ...(transferFallback || {}),
      ...guide,
      from_warehouse: transferFallback?.from_warehouse || guide.warehouse?.name || 'N/A',
      to_warehouse: transferFallback?.to_warehouse || guide.destination_warehouse?.name || 'N/A',
      has_writeoff: Boolean(guide.transfer_writeoff_entry_guide_id),
    };

    setSelectedTransfer(mergedTransfer);
    setPendingItems(calculatePendingItems(guide));
    return mergedTransfer;
  };

  const handleViewPendingItems = async (transfer) => {
    try {
      setLoading(true);
      await refreshSelectedTransferPending(transfer.id, transfer);
      setIsPendingItemsModalOpen(true);
    } catch (error) {
      console.error('Error al cargar pendientes del traspaso:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar los ítems pendientes',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const openWriteOffConfirm = () => {
    setWriteOffEntryDate(getCurrentDateLima());
    setWriteOffEntryTime(getCurrentTimeLima());
    setIsWriteOffConfirmOpen(true);
  };

  const executeWriteOffPending = async () => {
    if (!selectedTransfer?.id) return;

    const entryDate = writeOffEntryDate?.trim();
    const entryTime = normalizeTime(writeOffEntryTime);
    if (!entryDate || !entryTime) {
      setToast({
        isOpen: true,
        message: 'Indique la fecha y la hora del registro',
        type: 'error',
      });
      return;
    }

    setWriteOffLoading(true);
    try {
      const response = await entryGuideService.writeOffTransferPending(selectedTransfer.id, {
        entry_date: entryDate,
        entry_time: entryTime,
      });
      if (response.success) {
        const entryCode = response.data?.entry_guide?.code;
        const exitCode = response.data?.exit_guide?.code;
        const registeredAt = response.data?.registered_date && response.data?.registered_time
          ? formatGuideEntryDateTimeLima(response.data.registered_date, response.data.registered_time)
          : null;
        setToast({
          isOpen: true,
          message: entryCode && exitCode
            ? `Baja registrada${registeredAt ? ` el ${registeredAt}` : ''}. Guías ${entryCode} y ${exitCode}.`
            : response.message || 'Pendiente dado por perdido correctamente',
          type: 'success',
        });
        setIsWriteOffConfirmOpen(false);
        await refreshSelectedTransferPending(selectedTransfer.id, selectedTransfer);
        await loadDocuments(selectedYear);
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'No se pudo dar por perdido el pendiente',
          type: 'error',
        });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al dar por perdido el pendiente',
        type: 'error',
      });
    } finally {
      setWriteOffLoading(false);
    }
  };

  const executeRevokeWriteOff = async () => {
    if (!selectedTransfer?.id) return;

    setWriteOffLoading(true);
    try {
      const response = await entryGuideService.revokeTransferWriteOff(selectedTransfer.id);
      if (response.success) {
        setToast({
          isOpen: true,
          message: response.message || 'Baja por pérdida eliminada. El pendiente quedó restaurado.',
          type: 'success',
        });
        setIsRevokeWriteOffConfirmOpen(false);
        await refreshSelectedTransferPending(selectedTransfer.id, selectedTransfer);
        await loadDocuments(selectedYear);
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'No se pudo eliminar la baja por pérdida',
          type: 'error',
        });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar la baja por pérdida',
        type: 'error',
      });
    } finally {
      setWriteOffLoading(false);
    }
  };

  const executeDelete = async () => {
    if (!pendingDelete.id) return;

    try {
      setLoading(true);
      const response = await entryGuideService.deleteEntryGuide(pendingDelete.id);
      
      if (response.success) {
        setToast({
          isOpen: true,
          message: `Guía ${pendingDelete.code} eliminada correctamente`,
          type: 'success'
        });
        
        // Recargar documentos
        await loadDocuments(selectedYear);
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar la guía',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al eliminar guía:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar la guía',
        type: 'error'
      });
    } finally {
      setLoading(false);
      setPendingDelete({ id: null, code: null });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-gray-500 mb-4">Cargando almacén...</div>
        </div>
      </div>
    );
  }

  if (!warehouse) {
    return null;
  }

  const currentDocuments = documents[activeTab] || [];
  const hasGuidesInTab = currentDocuments.length > 0;
  const hasFilteredGuides = filteredDocuments.length > 0;
  const selectedWriteOffInfo = selectedTransfer
    ? getTransferWriteOffInfo(selectedTransfer)
    : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">{warehouse.name}</h1>
                <p className="text-gray-600 mt-1">Código: {warehouse.code}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => navigate('/warehouses')}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md font-medium transition-colors"
              >
                ← Volver a Almacenes
              </button>
              {kardexAuditModuleEnabled && (
                <Link
                  to={`/warehouses/${id}/inventory-count`}
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
                >
                  Toma de inventario
                </Link>
              )}
              </div>
            </div>

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-4">
              <span className="hover:text-teal-600 cursor-pointer" onClick={() => navigate('/dashboard')}>Dashboard</span>
              <span className="mx-2">/</span>
              <span className="hover:text-teal-600 cursor-pointer" onClick={() => navigate('/warehouses')}>Almacenes</span>
              <span className="mx-2">/</span>
              <span className="text-gray-800">{warehouse.name}</span>
            </nav>
          </div>

          {availableYears.length > 0 && (
            <div className="bg-white rounded-lg shadow-md mb-4 px-6 py-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-600 mr-1">Año (fecha kardex):</span>
                  {availableYears.map((year) => (
                    <button
                      key={year}
                      type="button"
                      onClick={() => setSelectedYear(year)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                        selectedYear === year
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
                {dateWarningCount > 0 && (
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm text-amber-700">
                      {dateWarningCount} guía{dateWarningCount === 1 ? '' : 's'} con fecha sospechosa en {selectedYear}
                    </span>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={onlyDateWarnings}
                        onChange={(e) => setOnlyDateWarnings(e.target.checked)}
                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      Solo con alerta de fecha
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-md mb-6">
            <div className="border-b border-gray-200 px-6 pt-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                <nav className="flex -mb-px min-w-0 flex-1 overflow-x-auto">
                  {[
                    { key: 'entrada', label: 'Guías de Entrada', icon: '📥' },
                    { key: 'inicial', label: 'Guías Iniciales', icon: '📋' },
                    { key: 'salida', label: 'Guías de Salida', icon: '📤' },
                    { key: 'traspaso', label: 'Traspasos', icon: '🔄' },
                    { key: 'recepcion', label: 'Recepciones', icon: '✅' },
                    { key: 'ajuste', label: 'Ajustes', icon: '⚖️' },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                        activeTab === tab.key
                          ? 'border-teal-500 text-teal-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span>{tab.icon}</span>
                      <span>{tab.label}</span>
                      {documents[tab.key]?.length > 0 && (
                        <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                          activeTab === tab.key
                            ? 'bg-teal-100 text-teal-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {documents[tab.key].length}
                        </span>
                      )}
                    </button>
                  ))}
                </nav>
                <MutateGate>
                  <button
                    type="button"
                    className="shrink-0 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors text-left sm:text-right"
                    onClick={() => {
                      if (activeTab === 'entrada') {
                        navigate(`/warehouses/${id}/entry-guides/create`);
                      } else if (activeTab === 'inicial') {
                        navigate(`/warehouses/${id}/initial-guides/create`);
                      } else if (activeTab === 'salida') {
                        navigate(`/warehouses/${id}/exit-guides/create`);
                      } else if (activeTab === 'traspaso') {
                        navigate(`/warehouses/${id}/transfer-guides/create`);
                      } else if (activeTab === 'recepcion') {
                        navigate(`/warehouses/${id}/reception-guides/create`);
                      } else if (activeTab === 'ajuste') {
                        navigate(`/warehouses/${id}/adjustment-guides/create`);
                      } else {
                        setToast({
                          isOpen: true,
                          message: 'Funcionalidad en desarrollo',
                          type: 'info',
                        });
                      }
                    }}
                  >
                    {getCreateButtonLabel()}
                  </button>
                </MutateGate>
              </div>
            </div>
          </div>

          {/* Tabla de Documentos */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {hasGuidesInTab && (
              <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[14rem]">
                  <label htmlFor="guide-filter" className="block text-xs font-medium text-gray-600 mb-1">
                    Buscar guía
                  </label>
                  <input
                    id="guide-filter"
                    type="text"
                    value={guideFilter}
                    onChange={(e) => setGuideFilter(e.target.value)}
                    placeholder="Código, entidad, referencia, usuario..."
                    className="w-full h-[38px] px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                {guideFilter.trim() !== '' && (
                  <button
                    type="button"
                    onClick={() => setGuideFilter('')}
                    className="h-[38px] px-3 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Limpiar
                  </button>
                )}
                <p className="text-sm text-gray-500 pb-2">
                  {hasFilteredGuides
                    ? `${filteredDocuments.length} resultado(s)`
                    : 'Sin coincidencias'}
                </p>
                <button
                  type="button"
                  onClick={handleExportExcel}
                  disabled={!hasFilteredGuides || exportingExcel}
                  className="h-[38px] px-4 text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-md hover:bg-teal-100 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {exportingExcel ? 'Exportando…' : 'Exportar Excel'}
                </button>
              </div>
            )}

            {!hasGuidesInTab ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No hay {getDocumentTypeLabel(activeTab).toLowerCase()} registrados</p>
              </div>
            ) : !hasFilteredGuides ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No hay guías que coincidan con la búsqueda</p>
              </div>
            ) : (
              <>
              <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Código
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Fecha y hora
                    </th>
                    {(activeTab === 'entrada' || activeTab === 'inicial' || activeTab === 'ajuste') && (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[14rem]">
                          Entidad
                        </th>
                        {activeTab === 'entrada' && (
                          <>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[12rem]">
                              Referencia
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                              Monto
                            </th>
                          </>
                        )}
                      </>
                    )}
                    {activeTab === 'salida' && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[14rem]">
                        Entidad
                      </th>
                    )}
                    {activeTab === 'salida' && ventasEnabled && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Venta
                      </th>
                    )}
                    {activeTab === 'traspaso' && (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Desde
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Hacia
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Guías
                        </th>
                      </>
                    )}
                    {activeTab === 'recepcion' && (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Desde
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Hacia
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Guía Traspaso
                        </th>
                        {showReceptionConsumeButton && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Consumo
                          </th>
                        )}
                      </>
                    )}
                    {activeTab === 'salida' && showReceptionConsumeButton && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recepción origen
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    {activeTab !== 'entrada' && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                    )}
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-[1%]">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedDocuments.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono text-teal-600">{doc.code}</span>
                          {hasDateWarnings(doc) && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"
                              title={formatDateWarningsTooltip(doc.date_warnings)}
                            >
                              Fecha
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm tabular-nums ${
                          hasDateWarnings(doc) ? 'text-amber-700 font-medium' : 'text-gray-500'
                        }`}
                        title={hasDateWarnings(doc) ? formatDateWarningsTooltip(doc.date_warnings) : undefined}
                      >
                        {formatGuideEntryDateTimeLima(doc.entry_date, doc.entry_time)}
                      </td>
                      {(activeTab === 'entrada' || activeTab === 'inicial' || activeTab === 'ajuste') && (
                        <>
                          <td className="px-6 py-4 text-sm text-gray-900 align-middle">
                            <div className="max-w-[14rem] min-w-0">
                              <span
                                className="block truncate"
                                title={doc.supplier || 'N/A'}
                              >
                                {doc.supplier || 'N/A'}
                              </span>
                            </div>
                          </td>
                          {activeTab === 'entrada' && (
                            <>
                              <td className="px-6 py-4 text-sm text-gray-500 align-top">
                                <div className="max-w-[12rem] min-w-0 space-y-1.5">
                                  {Array.isArray(doc.references) && doc.references.length > 0 ? (
                                    doc.references.map((ref, idx) => (
                                      <div key={`${ref.code || ref.label}-${idx}`} className="min-w-0">
                                        <div className="truncate font-medium text-gray-700" title={ref.label}>
                                          {ref.label}
                                        </div>
                                        {ref.code && (
                                          <div className="text-xs text-gray-400 truncate" title={ref.code}>
                                            {ref.code}
                                          </div>
                                        )}
                                        {ref.amount != null && Number(ref.amount) > 0 && (
                                          <div className="text-xs text-teal-700">
                                            S/ {Number(ref.amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </div>
                                        )}
                                      </div>
                                    ))
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900 align-top whitespace-nowrap">
                                <div className="font-medium">
                                  S/ {Number(doc.total_amount || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                {doc.billing_invoiced_amount != null && (
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    Fact. S/ {Number(doc.billing_invoiced_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    {Number(doc.billing_pending_amount) > 0.01 && (
                                      <span className="text-amber-700">
                                        {' '}· Pend. S/ {Number(doc.billing_pending_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                            </>
                          )}
                        </>
                      )}
                      {activeTab === 'salida' && (
                        <td className="px-6 py-4 text-sm text-gray-900 align-top">
                          <div className="max-w-[14rem] min-w-0">
                            <span className="block truncate" title={doc.supplier || 'N/A'}>
                              {doc.supplier}
                            </span>
                          </div>
                        </td>
                      )}
                      {activeTab === 'salida' && ventasEnabled && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {doc.sales_order_code ? (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800"
                              title="Venta vinculada"
                            >
                              {doc.sales_order_code}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      )}
                      {activeTab === 'traspaso' && (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {doc.from_warehouse}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {doc.to_warehouse}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {doc.has_receptions ? (
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-[100px]">
                                  <div
                                    className={`h-2 rounded-full transition-all ${
                                      doc.completion_percentage >= 100
                                        ? 'bg-green-500'
                                        : doc.completion_percentage > 0
                                        ? 'bg-yellow-500'
                                        : 'bg-gray-300'
                                    }`}
                                    style={{ width: `${Math.min(100, doc.completion_percentage)}%` }}
                                  />
                                </div>
                                <span className="text-xs font-medium text-gray-600 min-w-[45px]">
                                  {doc.completion_percentage.toFixed(0)}%
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </td>
                        </>
                      )}
                      {activeTab === 'recepcion' && (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {doc.from_warehouse}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {doc.to_warehouse}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {doc.transfer_guide_code}
                          </td>
                          {showReceptionConsumeButton && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {doc.is_consumed && doc.consume_exit_guide_code ? (
                                <button
                                  type="button"
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200"
                                  title="Ver guía de salida generada por el consumo"
                                  onClick={() => navigate(`/exit-guides/${doc.consume_exit_guide_id}/edit`)}
                                >
                                  Consumido → {doc.consume_exit_guide_code}
                                </button>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                          )}
                        </>
                      )}
                      {activeTab === 'salida' && showReceptionConsumeButton && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {doc.reception_source_code ? (
                            <button
                              type="button"
                              className="text-amber-700 hover:text-amber-900 font-medium"
                              title="Ver recepción origen del consumo"
                              onClick={() => navigate(`/reception-guides/${doc.reception_source_guide_id}/edit`)}
                            >
                              ← {doc.reception_source_code}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {doc.items_count} items
                      </td>
                      {activeTab !== 'entrada' && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(doc.status)}
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap align-middle">
                        <div className="inline-flex items-center justify-end gap-2">
                          {activeTab === 'traspaso' && (doc.completion_percentage < 100 || doc.has_writeoff) && (
                            <button
                              className="text-orange-600 hover:text-orange-900"
                              onClick={() => handleViewPendingItems(doc)}
                              title={doc.has_writeoff ? 'Ver baja por pérdida registrada' : 'Ver items pendientes por entregar'}
                            >
                              {doc.has_writeoff ? 'Baja' : 'Pendientes'}
                            </button>
                          )}
                          {activeTab === 'recepcion' && showReceptionConsumeButton && !doc.is_consumed && (
                            <ReceptionConsumeButton
                              variant="link"
                              onClick={() => handleConsumeReception(doc)}
                            />
                          )}
                          {activeTab !== 'ajuste' && (
                            <button
                              className="text-purple-600 hover:text-purple-900"
                              onClick={() => {
                                if (activeTab === 'entrada') {
                                  navigate(`/entry-guides/${doc.id}/edit`);
                                } else if (activeTab === 'inicial') {
                                  navigate(`/initial-guides/${doc.id}/edit`);
                                } else if (activeTab === 'salida') {
                                  navigate(`/exit-guides/${doc.id}/edit`);
                                } else if (activeTab === 'traspaso') {
                                  navigate(`/transfer-guides/${doc.id}/edit`);
                                } else if (activeTab === 'recepcion') {
                                  navigate(`/reception-guides/${doc.id}/edit`);
                                } else {
                                  setToast({
                                    isOpen: true,
                                    message: 'Funcionalidad en desarrollo',
                                    type: 'info'
                                  });
                                }
                              }}
                            >
                              Ver
                            </button>
                          )}
                          <EntryGuidePrintButton onClick={() => handlePrintGuide(doc.id, doc)} />
                          <EntryGuidePdfButton
                            guideId={doc.id}
                            guideType={doc.guide_type || activeTab}
                            itemsCount={doc.items_count ?? 0}
                            guideCode={doc.code}
                            onSuccess={(guide) => setToast({
                              isOpen: true,
                              message: `PDF de ${guide.code} descargado`,
                              type: 'success',
                            })}
                            onError={(message) => setToast({
                              isOpen: true,
                              message: message || 'Error al generar el PDF',
                              type: 'error',
                            })}
                          />
                          {canMutate && !doc.is_production_special && (
                            <button
                              type="button"
                              className="text-red-600 hover:text-red-900"
                              onClick={() => handleDelete(doc.id, doc.code)}
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              {filteredDocuments.length > GUIDES_PAGE_SIZE && (
                <div className="px-6 py-4 border-t border-gray-200 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-gray-600">
                    Mostrando {(currentPage - 1) * GUIDES_PAGE_SIZE + 1}–
                    {Math.min(currentPage * GUIDES_PAGE_SIZE, filteredDocuments.length)} de {filteredDocuments.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage <= 1}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    <span className="text-sm text-gray-600 tabular-nums">
                      Página {currentPage} de {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
              </>
            )}
          </div>
        </div>
      </div>

      <ReceptionConsumeModal
        isOpen={Boolean(consumeModalGuide)}
        guideId={consumeModalGuide?.id}
        guideCode={consumeModalGuide?.code}
        onClose={() => setConsumeModalGuide(null)}
        onSuccess={(data) => {
          const exitCode = data?.exit_guide?.code;
          setToast({
            isOpen: true,
            message: exitCode
              ? `Consumo registrado. Guía de salida ${exitCode} creada.`
              : 'Consumo registrado exitosamente',
            type: 'success',
          });
          loadDocuments(selectedYear);
        }}
        onError={(message) => {
          setToast({
            isOpen: true,
            message: message || 'Error al consumir la recepción',
            type: 'error',
          });
        }}
      />

      {/* Toast */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />

      {/* Modal de confirmación de eliminación */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setPendingDelete({ id: null, code: null });
        }}
        onConfirm={executeDelete}
        title="Confirmar Eliminación"
        message={pendingDelete.code ? `¿Está seguro de que desea eliminar la guía ${pendingDelete.code}? Esta acción no se puede deshacer.` : '¿Está seguro de que desea eliminar esta guía? Esta acción no se puede deshacer.'}
        confirmText="Aceptar"
        cancelText="Cancelar"
      />

      {/* Modal de items pendientes por entregar */}
      {isPendingItemsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">
                  Pendientes por Entregar - {selectedTransfer?.code}
                </h3>
                <button
                  onClick={() => {
                    setIsPendingItemsModalOpen(false);
                    setSelectedTransfer(null);
                    setPendingItems([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {selectedTransfer && (
                <div className="mt-2 text-sm text-gray-600">
                  <p>Desde: {selectedTransfer.from_warehouse} → Hacia: {selectedTransfer.to_warehouse}</p>
                  <p>Fecha: {formatGuideEntryDateTimeLima(selectedTransfer.entry_date || selectedTransfer.date, selectedTransfer.entry_time) || 'N/A'}</p>
                </div>
              )}
            </div>
            <div className="p-6 space-y-6">
              {selectedWriteOffInfo && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-red-900">Baja por pérdida registrada</p>
                      <p className="text-sm text-red-800 mt-1">
                        Fecha y hora:{' '}
                        <span className="font-medium">
                          {formatGuideEntryDateTimeLima(
                            selectedWriteOffInfo.registered_date,
                            selectedWriteOffInfo.registered_time
                          ) || 'N/A'}
                        </span>
                      </p>
                      <p className="text-sm text-red-800 mt-1">
                        Guías kardex: entrada{' '}
                        <span className="font-mono font-medium">{selectedWriteOffInfo.entry_code || '—'}</span>
                        {' '}y salida{' '}
                        <span className="font-mono font-medium">{selectedWriteOffInfo.exit_code || '—'}</span>
                      </p>
                      <p className="text-xs text-red-700 mt-2">
                        Estas guías especiales solo pueden eliminarse desde esta pantalla. Al deshacer la baja se recalcula el kardex.
                      </p>
                    </div>
                  </div>
                  {selectedWriteOffInfo.items.length > 0 && (
                    <div className="overflow-x-auto border border-red-100 rounded-lg bg-white">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Cant. dada por perdida</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unidad</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {selectedWriteOffInfo.items.map((item, index) => (
                            <tr key={`${item.product_id}-${index}`}>
                              <td className="px-4 py-2 text-sm text-gray-900">{item.product_name}</td>
                              <td className="px-4 py-2 text-sm text-gray-500">{item.product_sku}</td>
                              <td className="px-4 py-2 text-sm text-center font-medium text-red-700">
                                {item.quantity.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-500">{item.unit_of_measure}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              {pendingItems.length === 0 && !selectedWriteOffInfo ? (
                <div className="text-center py-8">
                  <svg className="w-16 h-16 mx-auto text-green-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-lg font-medium text-gray-700">Todos los items han sido entregados</p>
                  <p className="text-sm text-gray-500 mt-2">No hay items pendientes por entregar para este traspaso.</p>
                </div>
              ) : pendingItems.length > 0 ? (
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <p className="text-sm font-medium text-yellow-800">
                        {pendingItems.length} {pendingItems.length === 1 ? 'item pendiente' : 'items pendientes'} por entregar
                      </p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Producto
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            SKU
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cant. Traspaso
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cant. Recibida
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cant. Pendiente
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Unidad
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Condición
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {pendingItems.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {item.product_name}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {item.product_sku}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900 font-medium">
                              {item.transfer_quantity.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-600">
                              {item.received_quantity.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-red-600 font-semibold">
                              {item.pending_quantity.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {item.unit_of_measure}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {item.condition === 'good' ? 'Bueno' : 
                               item.condition === 'damaged' ? 'Dañado' : 
                               item.condition === 'defective' ? 'Defectuoso' : item.condition}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
            <div className="p-6 border-t border-gray-200 flex flex-wrap justify-between gap-3">
              <p className="text-xs text-gray-500 max-w-xl">
                {selectedTransfer?.has_writeoff
                  ? 'La baja por pérdida genera guías especiales en kardex (GPR/GPS). Solo puede eliminarse desde aquí; al hacerlo se recalcula el kardex.'
                  : 'Dar por perdido devuelve el pendiente al almacén origen y registra una salida inmediata en kardex con guías especiales.'}
              </p>
              <div className="flex gap-2">
                {canMutate && selectedTransfer?.has_writeoff && (
                  <button
                    type="button"
                    onClick={() => setIsRevokeWriteOffConfirmOpen(true)}
                    disabled={writeOffLoading}
                    className="px-4 py-2 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors font-medium"
                  >
                    Deshacer baja
                  </button>
                )}
                {canMutate && pendingItems.length > 0 && !selectedTransfer?.has_writeoff && (
                  <button
                    type="button"
                    onClick={openWriteOffConfirm}
                    disabled={writeOffLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors font-medium"
                  >
                    Dar por perdido
                  </button>
                )}
                <button
                  onClick={() => {
                    setIsPendingItemsModalOpen(false);
                    setSelectedTransfer(null);
                    setPendingItems([]);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isWriteOffConfirmOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]"
          onClick={writeOffLoading ? undefined : () => setIsWriteOffConfirmOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="p-6">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
                Dar por perdido el pendiente
              </h3>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed text-center">
                {selectedTransfer
                  ? `Se registrará una entrada (reversa) y una salida inmediata en el almacén origen por ${pendingItems.length} ítem(s) pendiente(s) del traspaso ${selectedTransfer.code}.`
                  : 'Se registrará la baja del pendiente del traspaso.'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de registro
                  </label>
                  <input
                    type="date"
                    value={writeOffEntryDate}
                    onChange={(e) => setWriteOffEntryDate(e.target.value)}
                    disabled={writeOffLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hora de registro
                  </label>
                  <input
                    type="time"
                    value={writeOffEntryTime}
                    onChange={(e) => setWriteOffEntryTime(e.target.value)}
                    disabled={writeOffLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsWriteOffConfirmOpen(false)}
                  disabled={writeOffLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={writeOffLoading}
                  onClick={executeWriteOffPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50"
                >
                  {writeOffLoading ? 'Procesando...' : 'Dar por perdido'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={isRevokeWriteOffConfirmOpen}
        onClose={() => !writeOffLoading && setIsRevokeWriteOffConfirmOpen(false)}
        onConfirm={executeRevokeWriteOff}
        title="Deshacer baja por pérdida"
        message={
          selectedTransfer && selectedWriteOffInfo
            ? `¿Eliminar la baja registrada el ${formatGuideEntryDateTimeLima(selectedWriteOffInfo.registered_date, selectedWriteOffInfo.registered_time) || '—'} (${selectedWriteOffInfo.entry_code || 'GPR'} / ${selectedWriteOffInfo.exit_code || 'GPS'})? Se restaurará el pendiente del traspaso ${selectedTransfer.code} y se recalculará el kardex.`
            : '¿Eliminar la baja por pérdida registrada? Se recalculará el kardex.'
        }
        confirmText={writeOffLoading ? 'Procesando...' : 'Deshacer baja'}
        cancelText="Cancelar"
        type="danger"
        isLoading={writeOffLoading}
        overlayClassName="z-[60]"
      />
    </div>
  );
};

export default WarehouseDocuments;

